import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Cog,
  ChevronRight,
  Shield,
  Package,
  Clock,
  Wrench,
  Boxes,
  CheckCircle2,
  Truck,
  Search,
  EllipsisVertical
} from 'lucide-react';
import img from "../../Images/plate.jpg";
import img1 from "../../Images/pump.webp";
import img2 from "../../Images/safety.jpg";
import img3 from '../../image2/control.webp';
import img4 from '../../image2/temp.jpg';
import img5 from "../../image2/ignite.jpg"
import { useNavigate } from 'react-router-dom';

const BoilerSpares = () => {
  const [activeCategory, setActiveCategory] = useState('mechanical');
  const navigate = useNavigate();
  
  const categories = [
    {
      id: 'mechanical',
      title: 'Mechanical Parts',
      icon: <Cog />,
      description: 'High-quality mechanical components for optimal performance',
      parts: [
        {
          name: 'Heat Exchanger',
          description: 'Primary heat exchanger for efficient heat transfer in domestic boilers',
          image: img,
          price: 'NPR',
          specs: 'NBR/EPDM Available'
        },
        {
          name: 'Pump Assembly',
          description: 'High-efficiency circulation pump with advanced flow control',
          image: img1,
          price: 'NPR',
          specs: 'NBR/EPDM Available'
        },
        {
          name: 'Pressure Relief Valve',
          description: 'Safety valve designed to regulate system pressure',
          image: img2,
          price: '$149.99',
          specs: 'NBR/EPDM Available'
        }
      ]
    },
    {
      id: 'electrical',
      title: 'Electrical Components',
      icon: <EllipsisVertical />,
      description: 'Premium electrical spares for reliable operation',
      parts: [
        {
          name: 'Control Board',
          description: 'Advanced electronic control board with diagnostic features',
          image: img3,
          price: 'NPR',
          specs: 'NBR/EPDM Available'
        },
        {
          name: 'Temperature Sensor',
          description: 'High-precision NTC sensor for accurate temperature control',
          image:img4,
          price: 'NPR',
          specs: 'NBR/EPDM Available'
        },
        {
          name: 'Ignition Electrode',
          description: 'Durable electrode for reliable flame ignition',
          image: img5,
          price: 'NPR',
          specs: 'NBR/EPDM Available'
        }
      ]
    },
    
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-[400px] bg-gradient-to-r from-indigo-950 to-indigo-800 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-[url('/grid.png')] opacity-20"></div>
        <div className="absolute inset-0 bg-indigo-950/50"></div>
        <div className="container mx-auto px-6 h-full flex flex-col justify-center relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Premium Boiler Spares
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-2xl">
              Genuine parts and components to ensure your boiler's longevity and performance
            </p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button className="bg-white text-indigo-950 px-6 py-2.5 rounded-lg font-semibold hover:bg-indigo-50 transition-colors duration-300 flex items-center group"
              onClick={() => navigate('/products/steam-accessories')}>
                Browse Catalog
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="border-2 border-white text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-300"
               onClick={() => navigate('/contact')}>
                Request Quote
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Animated Gear Background */}
        <motion.div
          className="absolute top-10 right-10 text-white/10"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <Cog size={120} />
        </motion.div>
        <motion.div
          className="absolute bottom-10 left-10 text-white/10"
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        >
          <Cog size={80} />
        </motion.div>
      </motion.section>

      {/* Features Grid */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <Shield />, title: 'Genuine Parts', text: 'Original manufacturer spares with warranty' },
              { icon: <Truck />, title: 'Fast Delivery', text: 'Nationwide delivery within 24-48 hours' },
              { icon: <Search />, title: 'Expert Support', text: 'Technical assistance in parts selection' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="text-indigo-600 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="container mx-auto">
          <motion.h2
            className="text-3xl font-bold text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
          >
            Spare Parts Categories
          </motion.h2>
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-indigo-50'
                }`}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.icon}
                {category.title}
              </motion.button>
            ))}
          </div>
          
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {categories
              .find(cat => cat.id === activeCategory)
              ?.parts.map((part, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg relative"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <img
                    src={part.image}
                    alt={part.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <div className="absolute top-4 right-4 bg-cyan-600 text-white px-3 py-1 rounded-full text-sm">
                    In Stock
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{part.name}</h4>
                  <p className="text-gray-600 mb-4">{part.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-indigo-600 font-bold">{part.price}</span>
                    <span className="text-gray-500 text-sm">{part.specs}</span>
                  </div>
                </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BoilerSpares;